import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Form, Button, Alert } from "react-bootstrap";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getCustomerDetails, getClientList, updateCustomerDetails } from "./CustomerView.actions";
import { getSensitiveFields } from "../ClientView/ClientView.actions";
import { CUSTOMER_UPDATE_SENSITIVE_FIELDS } from "../../../lookup/CustomerViewConstants";
import Spinner from "components/Spinner";
import ServerSideReactTable from "commons/table/ServerSideReactTable";
import { ColumnTypes } from "commons/table/Constants";
import { FilterTypes } from "commons/table/Constants";
import './CustomerView.css';
import { DEFAULT_PAGE_SIZE } from "../CustomerList/Customer.reducer";
import CreateClientModal from "./CreateClientModal";
import ConfirmationModal from "components/ConfirmationModal";
import { FaEye, FaPlus } from "react-icons/fa";
import { formatPhoneNumber } from "services/UtilityService";
import { hasRoleIn } from "rbac/RbacHelper";
import PERMISSIONS from "rbac/Permissions.json";
import { useConfigForm } from "../configTool/useConfigForm";
import { validateCustomerEditForm, normalizePhoneDigits } from "../configTool/configFormValidators";
import {
  ConfigReadOnlyBanner,
  ConfigFormLabel,
  ConfigFormSwitch,
  ConfigSection,
  SensitiveFieldsConfirmationBody,
} from "../configTool/ConfigFormComponents";
import LogoUrlField from "../configTool/LogoUrlField";
import ConfigStickyActionBar from "../configTool/ConfigStickyActionBar";
import ConfigBreadcrumb from "../configTool/ConfigBreadcrumb";
import { useUnsavedChangesWarning } from "../configTool/useUnsavedChangesWarning";

const CustomerView = () => {
  const { customerInfoUuid } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    details: customerDetails,
    loadingDetails,
    clients,
    loadingClients,
    clientListState,
    pages,
    totalElements,
    updating,
    sensitiveFields,
  } = useSelector(
    (state) => state.customerView
  );

  // Ignore stale Redux details from a previously opened customer.
  // Detail API uses customerInfoUuid (list API uses id).
  const customer =
    customerDetails &&
    String(customerDetails.customerInfoUuid || customerDetails.id) === String(customerInfoUuid)
      ? customerDetails
      : null;

  const [showCreateClientModal, setShowCreateClientModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [changedSensitiveFields, setChangedSensitiveFields] = useState([]);

  const canManage = hasRoleIn([PERMISSIONS.PERM_CONFIG_TOOL_MANAGE]);
  const isReadOnly = !canManage;

  const initialFormValues = useMemo(() => {
    if (!customer) return null;
    return {
      ...customer,
      supportNumber: normalizePhoneDigits(customer.supportNumber),
      faxNumberInbound: normalizePhoneDigits(customer.faxNumberInbound),
      showSavingPrices: customer.showSavingPrices !== undefined ? customer.showSavingPrices : true,
      botCallAllowed: customer.botCallAllowed === true,
      botTaskAllowed: customer.botTaskAllowed === true,
      botPlanName: customer.botPlanName ?? '',
    };
  }, [customer]);

  const [botCallingEnabled, setBotCallingEnabled] = useState(false);

  useEffect(() => {
    setBotCallingEnabled(
      (initialFormValues?.botCallAllowed || initialFormValues?.botTaskAllowed) === true
    );
  }, [initialFormValues]);

  const validate = useCallback(
    (state) => validateCustomerEditForm(state, { botCallingEnabled }),
    [botCallingEnabled]
  );

  const {
    formState,
    originalFormState,
    errors,
    isDirty,
    handleInputChange,
    handleCheckboxChange,
    handleBlur,
    shouldShowError,
    runValidation,
  } = useConfigForm({
    initialValues: initialFormValues,
    validate,
    entityKey: customerInfoUuid,
  });

  const { confirmDiscard, allowNextNavigation } = useUnsavedChangesWarning(canManage && isDirty);

  const handleBotCallingToggle = (event) => {
    const { checked } = event.target;
    setBotCallingEnabled(checked);
    if (!checked) {
      handleCheckboxChange({ target: { name: "botCallAllowed", checked: false } });
      handleCheckboxChange({ target: { name: "botTaskAllowed", checked: false } });
    }
  };

  useEffect(() => {
    dispatch(getCustomerDetails(customerInfoUuid));
    dispatch(getClientList(customerInfoUuid, clientListState.pageSize, clientListState.page, clientListState.sorted, clientListState.filtered));
    dispatch(getSensitiveFields(CUSTOMER_UPDATE_SENSITIVE_FIELDS));
  }, [dispatch, customerInfoUuid]);

  const onUpdateClientTable = (filters, sort, pageSize, page) => {
    dispatch(getClientList(customerInfoUuid, pageSize, page, sort, filters));
  };

  const handleClientCreated = () => {
    onUpdateClientTable(clientListState.filtered, clientListState.sorted, clientListState.pageSize, clientListState.page);
  };

  const handleCancel = () => {
    if (!confirmDiscard()) return;
    navigate("/admin/customers");
  };

  const proceedWithSave = () => {
    dispatch(updateCustomerDetails(customerInfoUuid, formState)).then(
      (response) => {
        if (response && response.type === "UPDATE_CUSTOMER_DETAILS_SUCCESS") {
          allowNextNavigation();
          navigate("/admin/customers");
        }
      }
    );
  };

  const handleSave = () => {
    if (!runValidation()) {
      return;
    }

    const changedFields = sensitiveFields.filter(
      (field) => originalFormState && originalFormState[field] !== formState[field]
    );
    if (changedFields.length > 0) {
      setChangedSensitiveFields(changedFields);
      setShowConfirmationModal(true);
    } else {
      proceedWithSave();
    }
  };

  const handleConfirmSave = () => {
    setShowConfirmationModal(false);
    proceedWithSave();
  };

  const handleClientRowClick = (row) => {
    const uuid = row?.original?.clientInfoUuid;
    if (uuid) {
      navigate(`/admin/client/${uuid}`, {
        state: {
          customerInfoUuid,
          customerName: customer?.customerName,
        },
      });
    }
  };

  const clientColumns = [
    {
      header: "Name",
      accessor: "clientName",
      accessorKey: "clientName",
      enableSorting: true,
      enableColumnFilter: true,
      ColumnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Client Abbreviation",
      accessor: "clientNameAbbreviation",
      accessorKey: "clientNameAbbreviation",
      enableSorting: true,
      enableColumnFilter: true,
      ColumnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Actions",
      accessor: "action",
      accessorKey: "action",
      enableSorting: false,
      enableColumnFilter: false,
      columnType: ColumnTypes.Custom,
      customComponent: (value, row) => {
        return (
          <Link
            to={`/admin/client/${row.original.clientInfoUuid}`}
            state={{
              customerInfoUuid,
              customerName: customer?.customerName,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <FaEye /> &nbsp; View
          </Link>
        );
      },
    },
  ];

  if (loadingDetails || !customer || !formState) {
    return <Spinner />;
  }

  const showClientsEmpty =
    !loadingClients && (!clients || clients.length === 0) && totalElements === 0;

  return (
    <Container fluid className="customer-view-page">
      <ConfirmationModal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirmSave}
        title="Confirm Save"
        body={<SensitiveFieldsConfirmationBody changedFields={changedSensitiveFields} />}
      />

      <ConfigBreadcrumb
        items={[
          { label: "Customers", to: "/admin/customers" },
          { label: customer.customerName },
        ]}
      />

      {canManage && (
        <ConfigStickyActionBar
          title={customer.customerName}
          subtitle="Customer configuration"
          onCancel={handleCancel}
          onSave={handleSave}
          isDirty={isDirty}
          isSaving={updating}
        />
      )}

      {!canManage && (
        <Row className="mb-3">
          <Col md={12}>
            <h5 className="mb-0">{customer.customerName}</h5>
          </Col>
        </Row>
      )}

      {isReadOnly && (
        <Row>
          <Col md={12}>
            <ConfigReadOnlyBanner />
          </Col>
        </Row>
      )}

      <Row>
        <Col md={12}>
          <div className="customer-details-wrapper">
            {updating && (
              <div className="updating-overlay">
                <Spinner />
              </div>
            )}

            <ConfigSection id="customer-identity" title="Identity">
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Customer Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" value={formState.customerName} disabled />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label>Program Name <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" name="programName" value={formState.programName} disabled />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Group>
                      <Form.Label>Customer Abbreviation <span className="text-danger">*</span></Form.Label>
                      <Form.Control type="text" value={formState.customerNameAbbreviation} disabled />
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                    <LogoUrlField
                      value={formState.logoUrl}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      disabled={isReadOnly}
                      sensitiveFields={sensitiveFields}
                      required
                      isInvalid={shouldShowError("logoUrl")}
                      error={errors.logoUrl}
                    />
                  </Col>
                </Row>
              </Form>
            </ConfigSection>

            <ConfigSection id="customer-contact" title="Contact">
              <Form>
                <Row>
                  <Col md={6}>
                    <Form.Group>
                      <ConfigFormLabel label="Support Number" fieldName="supportNumber" sensitiveFields={sensitiveFields} />
                      <Form.Control
                        type="text"
                        name="supportNumber"
                        value={formatPhoneNumber(formState.supportNumber || "")}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="e.g. (555) 555-5555"
                        maxLength={14}
                        disabled={isReadOnly}
                        isInvalid={shouldShowError("supportNumber")}
                      />
                      {shouldShowError("supportNumber") && (
                        <Form.Control.Feedback type="invalid">{errors.supportNumber}</Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <ConfigFormLabel label="Inbound Fax Number" fieldName="faxNumberInbound" sensitiveFields={sensitiveFields} />
                      <Form.Control
                        type="text"
                        name="faxNumberInbound"
                        value={formatPhoneNumber(formState.faxNumberInbound || "")}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        placeholder="e.g. (555) 555-5555"
                        maxLength={14}
                        disabled={isReadOnly}
                        isInvalid={shouldShowError("faxNumberInbound")}
                      />
                      {shouldShowError("faxNumberInbound") && (
                        <Form.Control.Feedback type="invalid">{errors.faxNumberInbound}</Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                  <Col md={6} className="mt-2">
                    <Form.Group>
                      <ConfigFormLabel label="Email" fieldName="email" sensitiveFields={sensitiveFields} />
                      <Form.Control
                        type="email"
                        name="email"
                        value={formState.email || ""}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                        disabled={isReadOnly}
                        isInvalid={shouldShowError("email")}
                      />
                      {shouldShowError("email") && (
                        <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                      )}
                    </Form.Group>
                  </Col>
                </Row>
              </Form>
            </ConfigSection>

            <ConfigSection id="customer-programs" title="Programs">
              <Form>
                <Row>
                  <Col md={6}>
                    <ConfigFormSwitch
                      label="Auto Accept"
                      fieldName="isAutoAcceptEnabled"
                      checked={formState.isAutoAcceptEnabled}
                      onChange={handleCheckboxChange}
                      id="auto-accept-switch"
                      disabled={isReadOnly}
                      sensitiveFields={sensitiveFields}
                    />
                  </Col>
                  <Col md={6}>
                    <ConfigFormSwitch
                      label="Intro Fax"
                      fieldName="isIntroFaxEnabled"
                      checked={formState.isIntroFaxEnabled}
                      onChange={handleCheckboxChange}
                      id="intro-fax-switch"
                      disabled={isReadOnly}
                      sensitiveFields={sensitiveFields}
                    />
                  </Col>
                </Row>
              </Form>
            </ConfigSection>

            <ConfigSection id="customer-calling" title="Automated Calling">
              <Form>
                <Row className="align-items-start">
                  <Col md={6}>
                    <ConfigFormSwitch
                      label="Bot Calling"
                      fieldName="botCallingEnabled"
                      checked={botCallingEnabled}
                      onChange={handleBotCallingToggle}
                      id="bot-calling-switch"
                      disabled={isReadOnly}
                    />
                    {botCallingEnabled && (
                      <div className="ml-4 mt-2">
                        <ConfigFormSwitch
                          label="Tasks"
                          fieldName="botTaskAllowed"
                          checked={formState.botTaskAllowed}
                          onChange={handleCheckboxChange}
                          id="bot-task-allowed-switch"
                          disabled={isReadOnly}
                          sensitiveFields={sensitiveFields}
                        />
                        <ConfigFormSwitch
                          label="Calls"
                          fieldName="botCallAllowed"
                          checked={formState.botCallAllowed}
                          onChange={handleCheckboxChange}
                          id="bot-call-allowed-switch"
                          disabled={isReadOnly}
                          sensitiveFields={sensitiveFields}
                        />
                        {shouldShowError("botCalling") && (
                          <div className="text-danger small mt-1">{errors.botCalling}</div>
                        )}
                      </div>
                    )}
                  </Col>
                  {botCallingEnabled && (
                    <Col md={6}>
                      <Form.Group className="d-flex align-items-center">
                        <ConfigFormLabel
                          label="Plan Name"
                          fieldName="botPlanName"
                          sensitiveFields={sensitiveFields}
                          className="mb-0 mr-3 flex-shrink-0"
                        />
                        <Form.Control
                          className="flex-grow-1"
                          type="text"
                          name="botPlanName"
                          value={formState.botPlanName}
                          onChange={handleInputChange}
                          onBlur={handleBlur}
                          disabled={isReadOnly}
                          placeholder="RxAdvocate"
                        />
                      </Form.Group>
                    </Col>
                  )}
                </Row>
              </Form>
            </ConfigSection>

            <ConfigSection id="customer-pricing" title="Pricing">
              <Form>
                <Row>
                  <Col md={6}>
                    <ConfigFormSwitch
                      label="Show Prices"
                      fieldName="showSavingPrices"
                      checked={formState.showSavingPrices}
                      onChange={handleCheckboxChange}
                      id="show-saving-prices-switch"
                      disabled={isReadOnly}
                      sensitiveFields={sensitiveFields}
                    />
                  </Col>
                </Row>
              </Form>
            </ConfigSection>

            <ConfigSection
              id="customer-clients"
              title={loadingClients ? "Clients" : `Clients (${totalElements})`}
              headerExtra={
                canManage ? (
                  <Button variant="primary" size="sm" onClick={() => setShowCreateClientModal(true)}>
                    <FaPlus className="mr-1" /> Create Client
                  </Button>
                ) : null
              }
            >
              {showClientsEmpty ? (
                <Alert variant="light" className="customer-clients-empty mb-0 text-center">
                  <p className="mb-2">
                    No clients yet — create the first client for <strong>{customer.customerName}</strong>
                  </p>
                  {canManage && (
                    <Button variant="outline-primary" size="sm" onClick={() => setShowCreateClientModal(true)}>
                      <FaPlus className="mr-1" /> Create Client
                    </Button>
                  )}
                </Alert>
              ) : (
                <ServerSideReactTable
                  columnDefination={clientColumns}
                  data={clients}
                  loading={loadingClients}
                  currentPage={clientListState.page}
                  totalPages={pages}
                  pageSize={clientListState.pageSize}
                  defaultPageSize={DEFAULT_PAGE_SIZE}
                  totalRecords={totalElements}
                  sorted={clientListState.sorted}
                  filters={clientListState.filtered}
                  onUpdateTableData={onUpdateClientTable}
                  onRowClick={handleClientRowClick}
                  trStyle={() => ({ cursor: "pointer" })}
                  enableMultiRowSelection={false}
                  dataTestId="customer-clients"
                />
              )}
            </ConfigSection>
          </div>
        </Col>
      </Row>

      <CreateClientModal
        show={showCreateClientModal}
        onHide={() => setShowCreateClientModal(false)}
        customerInfoUuid={customerInfoUuid}
        customerName={customer?.customerName}
        onSuccess={handleClientCreated}
      />
    </Container>
  );
};

export default CustomerView;
